import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBlueprintContent, getBlueprintContentById } from "../../api/BlueprintApi";
import AuthenticatedLayout from "../../layouts/AuthenticatedLayout";
import { toastrOnTopCenter } from "../../utils/toastr";

const Blueprint = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [allContent, setAllContent] = useState(null);

  useEffect(() => {
    if (uuid) {
      fetchSingleContent();
    } else {
      fetchAllContent();
    }
  }, [uuid]);

  const fetchSingleContent = async () => {
    setLoading(true);
    try {
      const response = await getBlueprintContentById(uuid);
      if (response.status && response.data) {
        setContent(response.data);
      }
    } catch (error) {
      console.error("Error fetching blueprint content:", error);
      toastrOnTopCenter("Failed to load blueprint content", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllContent = async () => {
    setLoading(true);
    try {
      const response = await getBlueprintContent();
      if (response.status && response.data) {
        setAllContent(response.data);
      }
    } catch (error) {
      console.error("Error fetching blueprint content:", error);
      toastrOnTopCenter("Failed to load blueprint content", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getVimeoEmbedUrl = (videoUrl) => {
    if (!videoUrl) return null;
    const match = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (match) {
      return `https://player.vimeo.com/video/${match[1]}`;
    }
    return null;
  };

  const getVimeoThumbnail = (videoUrl) => {
    if (!videoUrl) return null;
    const match = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (match) {
      return `https://vumbnail.com/${match[1]}.jpg`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  // Single content view
  if (content && uuid) {
    const embedUrl = content.video_url && content.video_source === "vimeo" 
      ? getVimeoEmbedUrl(content.video_url) 
      : null;

    return (
      <div className="container-fluid py-5" style={{ backgroundColor: "#1a1a1a", minHeight: "100vh", color: "#fff" }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <h1 className="display-4 mb-4 font-weight-bold">{content.title}</h1>
              
              {content.description && (
                <p className="lead mb-4">{content.description}</p>
              )}

              {content.type === "case_study" && content.case_study_amount && (
                <div className="mb-4">
                  <h2 className="display-5 font-weight-bold text-success">
                    {formatAmount(content.case_study_amount)} {content.case_study_period}
                  </h2>
                </div>
              )}

              {embedUrl && (
                <div className="mb-4" style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
                  <iframe
                    src={embedUrl}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: 0,
                    }}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {content.image_url && !embedUrl && (
                <div className="mb-4">
                  <img 
                    src={content.image_url} 
                    alt={content.title}
                    className="img-fluid rounded"
                    style={{ maxHeight: "500px", width: "100%", objectFit: "cover" }}
                  />
                </div>
              )}

              {content.content && (
                <div className="mb-4">
                  {typeof content.content === "string" ? (
                    <div dangerouslySetInnerHTML={{ __html: content.content }} />
                  ) : (
                    <pre className="text-white">{JSON.stringify(content.content, null, 2)}</pre>
                  )}
                </div>
              )}

              <div className="mt-4">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate("/affiliate-links")}
                >
                  Back to Affiliate Links
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full blueprint page view
  if (allContent) {
    return (
      <div className="container-fluid p-0" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        {/* Header Section - White Background with Two Columns */}
        <div className="row mb-0" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
          <div className="col-lg-12 mx-auto" style={{ maxWidth: "95%" }}>
            <div className="row align-items-center">
              {/* Card Container */}
              <div className="col-12">
                <div className="d-flex flex-column flex-lg-row" style={{ 
                  backgroundColor: "#fff", 
                  border: "1px solid #e0e0e0", 
                  borderRadius: "12px",
                  padding: "50px",
                  gap: "50px",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08)",
                  transition: "box-shadow 0.3s ease"
                }}>
                  {/* Left Column - Text */}
                  <div style={{ flex: "1", minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <h1 className="mb-4 font-weight-bold text-dark" style={{ 
                      lineHeight: "1.2",
                      fontFamily: "sans-serif",
                      marginBottom: "24px"
                    }}>
                      <span style={{ fontSize: "2rem", fontWeight: "bold", color: "#2c3e50" }}>
                        HOW TO PROPERLY
                      </span>
                      <br />
                      <span style={{ fontSize: "2rem", fontWeight: "bold", color: "#2c3e50" }}>
                        PREPARE FOR
                      </span>
                      <br />
                      <span style={{ fontSize: "4rem", fontWeight: "bold", color: "#1a1a1a" }}>
                        OUR CALL:
                      </span>
                    </h1>
                    <p style={{ 
                      fontSize: "1.1rem", 
                      color: "#444", 
                      lineHeight: "1.7",
                      marginBottom: "20px",
                      textAlign: "left"
                    }}>
                      Please be in a quiet place without distractions, preferably with a computer, on the day & time your call is scheduled with a pen & paper to take notes.
                    </p>
                    <p style={{ 
                      fontSize: "1.1rem", 
                      color: "#444", 
                      lineHeight: "1.7",
                      textAlign: "left"
                    }}>
                      Please respect our time and your time as well, we have a strict policy with people who decide to book calls and not show up...
                    </p>
                  </div>
                  {/* Right Column - Image */}
                  <div style={{ 
                    flex: "1", 
                    minWidth: 0,
                    display: "flex",
                    alignItems: "stretch",
                    justifyContent: "center"
                  }}>
                    <img 
                      src="https://racetofreedom.nyc3.cdn.digitaloceanspaces.com/course-thumbnail/OpFaZyCORDAJ3GBThMNhBpcmWVElaGgX0E7cykvt.png" 
                      alt="Presenter" 
                      style={{ 
                        width: "100%", 
                        height: "auto",
                        objectFit: "cover",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* STEP 1: Overview Video - Black Background */}
        {allContent.overview_videos && allContent.overview_videos.length > 0 && (
          <div className="row mb-0" style={{ backgroundColor: "#000", padding: "80px 0", color: "#fff" }}>
            <div className="col-lg-10 mx-auto">
              <h2 className="text-center mb-3" style={{ fontSize: "3rem", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" }}>STEP 1</h2>
              <h3 className="text-center mb-4" style={{ fontSize: "1.8rem", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" }}>Full Overview of Our Offer and How Everything Works</h3>
              <div className="text-center py-3 mb-4" style={{ 
                fontSize: "1.3rem", 
                fontWeight: "bold", 
                backgroundColor: "#007bff", 
                color: "#fff", 
                border: "none",
                borderRadius: "8px",
                padding: "20px"
              }}>
                Click Below To Watch This Video First! (Sound On!)
              </div>
              {allContent.overview_videos.map((video, idx) => {
                const embedUrl = video.video_url && video.video_source === "vimeo" 
                  ? getVimeoEmbedUrl(video.video_url) 
                  : null;
                return (
                  <div key={idx} className="mb-4" style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: "8px" }}>
                    {embedUrl ? (
                      <iframe
                        src={embedUrl}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          border: 0,
                        }}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "#000" }}>
                        <p>Video not available</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2: Common Questions - Black Background */}
        {allContent.common_questions && allContent.common_questions.length > 0 && (
          <div className="row mb-0" style={{ backgroundColor: "#000", padding: "80px 0", color: "#fff" }}>
            <div className="col-lg-10 mx-auto">
              <h2 className="text-center mb-3" style={{ fontSize: "3rem", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" }}>STEP 2</h2>
              <h3 className="text-center mb-4" style={{ fontSize: "2rem", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" }}>Most Common Questions</h3>
              {allContent.common_questions.map((question, idx) => {
                const questionData = typeof question.content === "string" 
                  ? JSON.parse(question.content) 
                  : question.content;
                const embedUrl = question.video_url && question.video_source === "vimeo" 
                  ? getVimeoEmbedUrl(question.video_url) 
                  : null;
                
                return (
                  <div key={idx} className="mb-5">
                    {questionData && (
                      <div style={{ color: "#fff", marginBottom: "20px" }}>
                        <h4 className="text-center mb-4" style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" }}>
                          {questionData.question || question.title}
                        </h4>
                      </div>
                    )}
                    {embedUrl ? (
                      <div className="mb-4" style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: "8px" }}>
                        <iframe
                          src={embedUrl}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            border: 0,
                          }}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : question.image_url ? (
                      <div className="mb-4">
                        <img
                          src={question.image_url}
                          alt={question.title || "Question"}
                          style={{
                            width: "100%",
                            height: "auto",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                          }}
                        />
                      </div>
                    ) : null}
                    {questionData && questionData.answer && (
                      <p style={{ fontSize: "1.1rem", color: "#fff", lineHeight: "1.8" }}>
                        {questionData.answer}
                      </p>
                    )}
                    {idx < allContent.common_questions.length - 1 && (
                      <hr className="my-5" style={{ borderColor: "#333" }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3: Call Confirmation - Black Background */}
        {allContent.call_confirmation && allContent.call_confirmation.length > 0 && (
          <div className="row mb-0" style={{ backgroundColor: "#000", padding: "80px 0", color: "#fff" }}>
            <div className="col-lg-10 mx-auto">
              <h2 className="text-center mb-3" style={{ fontSize: "3rem", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" }}>STEP 3</h2>
              <h3 className="text-center mb-4" style={{ fontSize: "2rem", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" }}>Confirm Your Call</h3>
              {allContent.call_confirmation.map((confirmation, idx) => {
                const confirmationData = typeof confirmation.content === "string" 
                  ? JSON.parse(confirmation.content) 
                  : confirmation.content;
                
                // Icon mapping for instructions
                const getInstructionIcon = (index) => {
                  if (index === 0) {
                    return <span style={{ color: "#28a745", fontSize: "1.5rem", marginRight: "12px" }}>✓</span>; // Green checkmark
                  } else if (index === 1) {
                    return <i className="fa fa-paperclip" style={{ color: "#fff", fontSize: "1.3rem", marginRight: "12px" }}></i>; // Paperclip
                  } else if (index === 2) {
                    return <i className="fa fa-lightbulb-o" style={{ color: "#ffc107", fontSize: "1.5rem", marginRight: "12px" }}></i>; // Yellow lightbulb
                  }
                  return null;
                };

                // Highlight "I'll be there!" in the third instruction
                const formatInstruction = (instruction, index) => {
                  if (index === 2 && instruction.includes("I'll be there!")) {
                    const parts = instruction.split("I'll be there!");
                    return (
                      <>
                        {parts[0]}
                        <span style={{ color: "#ff9800", fontWeight: "bold" }}>I'll be there!</span>
                        {parts[1]}
                      </>
                    );
                  }
                  return instruction;
                };
                
                return (
                  <div key={idx}>
                    {/* Intro text with calendar icon */}
                    <div className="mb-5" style={{ display: "flex", alignItems: "flex-start", fontSize: "1.2rem", lineHeight: "1.8" }}>
                      <span style={{ 
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#dc3545",
                        color: "#fff",
                        borderRadius: "4px",
                        padding: "6px 10px",
                        fontSize: "1rem",
                        fontWeight: "bold",
                        marginRight: "12px",
                        minWidth: "40px",
                        height: "40px",
                        textAlign: "center"
                      }}>📅</span>
                      <p style={{ margin: 0, color: "#fff", flex: 1 }}>
                        <strong>You will receive a calendar invite via email (Google, Apple, or Outlook). It will look just like the one shown below.</strong>
                      </p>
                    </div>

                    {/* Calendar invite image */}
                    {confirmation.image_url && (
                      <div className="mb-5" style={{ textAlign: "center" }}>
                        <img 
                          src={confirmation.image_url} 
                          alt="Calendar Invite"
                          style={{ 
                            maxWidth: "100%",
                            height: "auto",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(255, 255, 255, 0.1)"
                          }}
                        />
                      </div>
                    )}

                    {/* Instructions with icons */}
                    {confirmationData && confirmationData.instructions && (
                      <div className="mb-4">
                        {confirmationData.instructions.map((instruction, i) => (
                          <div key={i} className="mb-4" style={{ 
                            display: "flex", 
                            alignItems: "flex-start",
                            fontSize: "1.1rem", 
                            color: "#fff", 
                            lineHeight: "1.8" 
                          }}>
                            {getInstructionIcon(i)}
                            <div style={{ flex: 1 }}>
                              <strong>{formatInstruction(instruction, i)}</strong>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Case Studies Section - White Background */}
        {allContent.case_studies && allContent.case_studies.length > 0 && (
          <div className="row mb-0" style={{ backgroundColor: "#fff", padding: "20px 0" }}>
            <div className="col-lg-12 mx-auto" style={{ padding: "0 15px" }}>
              <h2 className="text-center mb-2" style={{ fontSize: "2.5rem", fontWeight: "bold", color: "#000", whiteSpace: "nowrap" }}>Full-Length Client Interviews</h2>
              <hr className="mb-2" style={{ borderColor: "#ddd", borderWidth: "1px" }} />
              
              {/* Display Case Studies with Embedded Videos */}
              {allContent.case_studies.map((caseStudy, idx) => {
                const embedUrl = caseStudy.video_url && caseStudy.video_source === "vimeo" 
                  ? getVimeoEmbedUrl(caseStudy.video_url) 
                  : null;
                
                // Format title - the title should already include the amount based on the image
                const displayTitle = caseStudy.case_study_title || caseStudy.title;
                
                return (
                  <div key={idx} className="mb-3">
                    <h3 className="text-center mb-2" style={{ 
                      fontSize: "2rem", 
                      fontWeight: "bold", 
                      color: "#000",
                      lineHeight: "1.2",
                      whiteSpace: "nowrap"
                    }}>
                      {displayTitle}
                    </h3>
                    
                    {/* Embedded Vimeo Video */}
                    {embedUrl ? (
                      <div className="mb-2" style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: "8px" }}>
                        <iframe
                          src={embedUrl}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            border: 0,
                          }}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : caseStudy.image_url ? (
                      <div className="mb-2 text-center">
                        <img
                          src={caseStudy.image_url}
                          alt={displayTitle}
                          style={{
                            width: "100%",
                            height: "auto",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                          }}
                        />
                      </div>
                    ) : null}
                    
                    {idx < allContent.case_studies.length - 1 && (
                      <hr className="my-3" style={{ borderColor: "#ddd", borderWidth: "1px" }} />
                    )}
                  </div>
                );
              })}

              {/* Additional Case Studies Section */}
              <div className="mt-2 mb-1" style={{ textAlign: "center" }}>
                <h2 className="mb-0" style={{ fontSize: "2rem", fontWeight: "bold", color: "#000", whiteSpace: "nowrap"}}>
                  …And Even More Case Studies
                </h2>
                <p className="mb-1" style={{ fontSize: "1.1rem", color: "#000", lineHeight: "1.8" }}>
                  Real results from real people — just like you. Scroll through the list below to see what's possible.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Testimonials/Social Posts Section - White Background */}
        {allContent.testimonials && allContent.testimonials.length > 0 && (
          <div className="row mb-0" style={{ backgroundColor: "#fff", padding: "20px 0" }}>
            <div className="col-lg-12 mx-auto" style={{ padding: "0 15px" }}>
              <div className="row mt-2">
                {/* Testimonial Posts - Left Column */}
                <div className="col-lg-6 mb-2">
                  {allContent.testimonials.filter((_, idx) => idx % 2 === 0).map((testimonial, idx) => {
                    const testimonialData = typeof testimonial.content === "string" 
                      ? JSON.parse(testimonial.content) 
                      : testimonial.content;
                    
                    
                    return (
                      <div key={testimonial.uuid || idx} className="card border-0 shadow-sm mb-2" style={{ borderRadius: "12px", backgroundColor: "#fff" }}>
                        <div className="card-body p-2">
                          <div className="d-flex align-items-center mb-1">
                            {testimonialData?.profile_image_url && (
                              <img 
                                src={testimonialData.profile_image_url} 
                                alt={testimonialData.author_name || "User"}
                                className="rounded-circle mr-3"
                                style={{ width: "50px", height: "50px", objectFit: "cover", border: "2px solid #e0e0e0" }}
                              />
                            )}
                          </div>
                          {testimonial.image_url && (
                            <img 
                              src={testimonial.image_url} 
                              alt={testimonial.title}
                              className="img-fluid mb-1 rounded"
                              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Testimonial Posts - Right Column */}
                <div className="col-lg-6 mb-2">
                  {allContent.testimonials.filter((_, idx) => idx % 2 === 1).map((testimonial, idx) => {
                    const testimonialData = typeof testimonial.content === "string" 
                      ? JSON.parse(testimonial.content) 
                      : testimonial.content;
                    
                    
                    return (
                      <div key={testimonial.uuid || idx} className="card border-0 shadow-sm mb-2" style={{ borderRadius: "12px", backgroundColor: "#fff" }}>
                        <div className="card-body p-2">
                          <div className="d-flex align-items-center mb-1">
                            {testimonialData?.profile_image_url && (
                              <img 
                                src={testimonialData.profile_image_url} 
                                alt={testimonialData.author_name || "User"}
                                className="rounded-circle mr-3"
                                style={{ width: "50px", height: "50px", objectFit: "cover", border: "2px solid #e0e0e0" }}
                              />
                            )}
                          </div>
                          {testimonial.image_url && (
                            <img 
                              src={testimonial.image_url} 
                              alt={testimonial.title}
                              className="img-fluid mb-1 rounded"
                              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Group Photo Section - Full Width */}
        {allContent.group_photo && (
          <div className="row mb-0" style={{ backgroundColor: "#fff", padding: "0" }}>
            <div className="col-12 p-0">
              <div style={{ position: "relative", paddingBottom: "40%", height: 0, overflow: "hidden" }}>
                <div 
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage: allContent.group_photo.image_url 
                      ? `url(${allContent.group_photo.image_url})` 
                      : "url(https://via.placeholder.com/1920x768?text=R2F+Community+Event)",
                    backgroundSize: "cover",
                    backgroundPosition: "center"
                  }}
                >
                  <div className="d-flex align-items-center justify-content-center" style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0,0,0,0.1)"
                  }}>
                    <div className="text-center text-white" style={{ zIndex: 1 }}>
                      {allContent.group_photo.content && (
                        <>
                          {(() => {
                            const groupPhotoData = typeof allContent.group_photo.content === "string" 
                              ? JSON.parse(allContent.group_photo.content) 
                              : allContent.group_photo.content;
                            return (
                              <>
                                <h2 className="display-4 mb-3" style={{ fontSize: "3rem", fontWeight: "bold", textShadow: "2px 2px 4px rgba(0,0,0,0.5)" }}>
                                  {groupPhotoData?.heading || "Join Our Community"}
                                </h2>
                                <p className="lead" style={{ fontSize: "1.5rem", textShadow: "2px 2px 4px rgba(0,0,0,0.5)" }}>
                                  {groupPhotoData?.subtitle || "Real results from real people — just like you"}
                                </p>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="text-center p-5">
      <p className="text-muted">No blueprint content available</p>
    </div>
  );
};

export default AuthenticatedLayout(Blueprint);

